import Modal from "/WebInterface/new-ui/modules/modal.js";
import {
  generateCustomForm,
  attachCustomFormTools,
  checkFilesRequired,
} from "/WebInterface/new-ui/modules/custom-form/custom-form.js";
import Loader from "/WebInterface/new-ui/modules/loader/loader.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { getCustomForm } from "/WebInterface/new-ui/assets/js/utils/api/apis.js";
import { validate } from "/WebInterface/new-ui/assets/js/utils/validate.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { ajax } from "/WebInterface/new-ui/assets/js/utils/ajax.js";

const extractEventParams = (eventInfo) => {
  const openParenIndex = eventInfo.indexOf("customEvent");
  if (openParenIndex === -1) {
    return { params: {}, config: {} };
  }

  const afterCustomEvent = eventInfo.substring(
    openParenIndex + "customEvent".length
  );
  const parenMatch = afterCustomEvent.match(/^\s*\(/);
  if (!parenMatch) {
    return { params: {}, config: {} };
  }

  let braceCount = 0;
  let bracketCount = 0;
  let inString = false;
  let stringChar = null;
  let paramsStart = parenMatch.index + parenMatch[0].length;
  let paramsEnd = -1;

  for (let i = paramsStart; i < afterCustomEvent.length; i++) {
    const char = afterCustomEvent[i];
    const prevChar = i > 0 ? afterCustomEvent[i - 1] : null;

    if (!inString && (char === '"' || char === "'")) {
      inString = true;
      stringChar = char;
    } else if (inString && char === stringChar && prevChar !== "\\") {
      inString = false;
      stringChar = null;
    }

    if (!inString) {
      if (char === "{") braceCount++;
      else if (char === "}") braceCount--;
      else if (char === "[") bracketCount++;
      else if (char === "]") bracketCount--;
      else if (char === "(") braceCount++;
      else if (char === ")" && braceCount === 0 && bracketCount === 0) {
        paramsEnd = i;
        break;
      }
    }
  }

  if (paramsEnd === -1) {
    return { params: {}, config: {} };
  }

  const paramsString = afterCustomEvent
    .substring(paramsStart, paramsEnd)
    .trim();

  let paramsObj = {};
  let configObj = {};

  const convertToJSON = (str) => {
    let result = str.trim();
    result = result.replace(/'/g, '"');
    result = result.replace(
      /([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g,
      '$1"$2":'
    );
    return result;
  };

  const configProperties = [
    "label_alignment",
    "show_calendar_icon",
    "show_quick_date_picker",
    "files_required",
    "form_title",
    "submit_button_label",
    "width",
    "height",
    "success_message",
  ];

  const separateConfigFromParams = (parsedObj) => {
    const separatedParams = {};
    const separatedConfig = {};
    for (const key in parsedObj) {
      if (configProperties.includes(key)) {
        separatedConfig[key] = parsedObj[key];
      } else {
        separatedParams[key] = parsedObj[key];
      }
    }
    return { separatedParams, separatedConfig };
  };

  try {
    let braceCount = 0;
    let bracketCount = 0;
    let inString = false;
    let stringChar = null;
    let splitIndex = -1;

    for (let i = 0; i < paramsString.length; i++) {
      const char = paramsString[i];
      const prevChar = i > 0 ? paramsString[i - 1] : null;

      if (!inString && (char === '"' || char === "'")) {
        inString = true;
        stringChar = char;
      } else if (inString && char === stringChar && prevChar !== "\\") {
        inString = false;
        stringChar = null;
      }

      if (!inString) {
        if (char === "{") braceCount++;
        else if (char === "}") braceCount--;
        else if (char === "[") bracketCount++;
        else if (char === "]") bracketCount--;
        else if (char === "," && braceCount === 0 && bracketCount === 0) {
          splitIndex = i;
          break;
        }
      }
    }

    if (splitIndex > 0) {
      const firstParam = paramsString.substring(0, splitIndex).trim();
      const secondParam = paramsString.substring(splitIndex + 1).trim();

      const paramsStringClean = convertToJSON(firstParam);
      paramsObj = JSON.parse(paramsStringClean);

      const configStringClean = convertToJSON(secondParam);
      configObj = JSON.parse(configStringClean);
    } else {
      const paramsStringClean = convertToJSON(paramsString);
      const parsedObj = JSON.parse(paramsStringClean);
      const { separatedParams, separatedConfig } =
        separateConfigFromParams(parsedObj);
      paramsObj = separatedParams;
      configObj = separatedConfig;
    }
  } catch (e) {
    try {
      const paramsStringClean = convertToJSON(paramsString);
      const parsedObj = JSON.parse(paramsStringClean);
      const { separatedParams, separatedConfig } =
        separateConfigFromParams(parsedObj);
      paramsObj = separatedParams;
      configObj = separatedConfig;
    } catch (e2) {
      // Silent fail, return empty objects
    }
  }

  return { params: paramsObj, config: configObj };
};

const sendCustomEvent = async (
  params,
  paths,
  formData = {},
  successMessage = null
) => {
  const obj = {
    command: "customEvent",
    random: Math.random(),
    c2f: utils.cookie.get("currentAuth"),
    ...params,
    ...formData,
    paths: paths,
  };

  const resp = await ajax.call({ data: obj, async: true });
  const xml = new DOMParser().parseFromString(resp, "text/xml");
  const result = utils.xmlToJson(xml);

  if (result?.commandResult?.response?.toLowerCase() === "success") {
    const message = successMessage || t("customEventInitiated");
    growl(message, t("success"), 5000);
    return true;
  }

  growl(
    t("thereWasSomeProblemWhileRunningCustomEvent"),
    t("failure"),
    5000,
    true
  );
  return false;
};

const showCustomForm = (customForm, eventInfo, paths) => {
  const formName = customForm.name;

  const { params: eventParams, config: eventConfig } =
    extractEventParams(eventInfo);

  const windowConfig = window.customFormSettings?.[formName] || {};
  const config = { ...windowConfig, ...eventConfig };

  const formTitle = config.form_title || t("customEvent");
  const submitButtonLabel = config.submit_button_label || "submit";

  const modalWidth = config.width
    ? typeof config.width === "number"
      ? `${config.width}px`
      : config.width
    : "800px";
  const modalHeight = config.height
    ? typeof config.height === "number"
      ? `${config.height}px`
      : config.height
    : "575px";

  const handleSubmit = async (dialog) => {
    const validateFields = validate(dialog.root, false);

    if (validateFields.length === 0) {
      const messageFormLoader = new Loader({});
      const formData = utils.serializeInputs(dialog.root, false, false);

      try {
        const successMessage = config.success_message || null;
        await sendCustomEvent(eventParams, paths, formData, successMessage);
      } finally {
        messageFormLoader.deleteLoader();
        dialog.hide(true);
      }
    }
  };

  const formContent = generateCustomForm(customForm, { paths, config });

  const customFormDialog = new Modal({
    title: formTitle,
    body: formContent,
    uniqueId: "custom-event-form-modal",
    minWidth: modalWidth,
    minHeight: modalHeight,
    buttons: [
      {
        label: submitButtonLabel,
        close: false,
        classes: ["mr-3"],
        onClick: () => {
          handleSubmit(customFormDialog);
        },
      },
    ],
  });

  attachCustomFormTools(customFormDialog.root, formName, config);
};

export const initCustomEventForm = (eventInfo, paths) => {
  getCustomForm("customForm", true).then(async (res) => {
    if (res) {
      const { config: eventConfig } = extractEventParams(eventInfo);
      const windowConfig = window.customFormSettings?.[res.name] || {};
      const mergedConfig = { ...windowConfig, ...eventConfig };

      if (!checkFilesRequired(res.name, paths, mergedConfig)) {
        growl(
          t("nothingSelected"),
          t("pleaseSelectAtLeastOneFileOrFolder"),
          5000,
          true
        );
        return;
      }
      showCustomForm(res, eventInfo, paths);
    } else {
      const customEventLoader = new Loader({});
      const { params, config: eventConfig } = extractEventParams(eventInfo);
      const windowConfig = window.customFormSettings?.["customForm"] || {};
      const mergedConfig = { ...windowConfig, ...eventConfig };
      const successMessage = mergedConfig.success_message || null;
      await sendCustomEvent(params, paths, {}, successMessage);
      customEventLoader.deleteLoader();
    }
  });
};
